import { useVirtualizer } from '@tanstack/react-virtual';
import { useRef, useEffect, useState, useCallback } from 'react';
import { MessageItem } from './MessageItem';
import { EmptyState } from '../ui/EmptyState';
import { Spinner } from '../ui/Spinner';

interface Message {
  id: string;
  conversation_id: string;
  sender_id: string;
  sender_username: string;
  sender_display_name: string | null;
  parent_message_id: string | null;
  content: string;
  created_at: string;
  updated_at: string;
  edited: boolean;
  last_edited_at: string | null;
  reactions?: ReactionSummary[];
}

interface ReactionSummary {
  emoji: string;
  count: number;
  user_reacted: boolean;
}

interface MessageListProps {
  messages: Message[];
  loading: boolean;
  currentUserId: string | undefined;
  editingMessageId: string | null;
  editContent: string;
  savingEdit: boolean;
  onEditContentChange: (content: string) => void;
  onStartEdit: (message: Message) => void;
  onSaveEdit: (messageId: string) => void;
  onCancelEdit: () => void;
  onDelete: (messageId: string) => void;
  onToggleReaction: (messageId: string, emoji: string, currentlyReacted: boolean) => void;
  onLoadWhoReacted: (messageId: string, emoji: string) => void;
  deletingMessageId: string | null;
  typingUsers: Set<string>;
  commonEmojis: string[];
}

export function MessageList({
  messages,
  loading,
  currentUserId,
  editingMessageId,
  editContent,
  savingEdit,
  onEditContentChange,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onDelete,
  onToggleReaction,
  onLoadWhoReacted,
  deletingMessageId,
  typingUsers,
  commonEmojis,
}: MessageListProps) {
  const parentRef = useRef<HTMLDivElement>(null);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const [hasNewMessages, setHasNewMessages] = useState(false);
  const prevMessageCount = useRef(messages.length);

  // Virtual scrolling setup
  const virtualizer = useVirtualizer({
    count: messages.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 100, // Estimate ~100px per message
    overscan: 5, // Render 5 items above/below viewport
  });

  // Track scroll position to determine if user is near bottom
  const handleScroll = useCallback(() => {
    if (!parentRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = parentRef.current;
    const nearBottom = scrollHeight - scrollTop - clientHeight < 150;
    setIsNearBottom(nearBottom);
    if (nearBottom) setHasNewMessages(false);
  }, []);

  // Auto-scroll to bottom only if user is near bottom
  useEffect(() => {
    if (messages.length > prevMessageCount.current) {
      if (isNearBottom && parentRef.current) {
        parentRef.current.scrollTop = parentRef.current.scrollHeight;
      } else if (messages.length > 0) {
        setHasNewMessages(true);
      }
    }
    prevMessageCount.current = messages.length;
  }, [messages.length, isNearBottom]);

  const scrollToBottom = useCallback(() => {
    if (parentRef.current) {
      parentRef.current.scrollTop = parentRef.current.scrollHeight;
      setHasNewMessages(false);
    }
  }, []);

  if (loading && messages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center bg-slate-900/30">
        <Spinner />
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center bg-slate-900/30">
        <EmptyState
          icon="💬"
          title="No messages yet"
          description="Send the first message to start the conversation!"
        />
      </div>
    );
  }

  const virtualItems = virtualizer.getVirtualItems();

  return (
    <div
      ref={parentRef}
      className="flex-1 overflow-y-auto bg-slate-900/30 p-4 relative"
      onScroll={handleScroll}
    >
      {/* New messages indicator */}
      {hasNewMessages && (
        <button
          onClick={scrollToBottom}
          className="sticky top-2 left-1/2 -translate-x-1/2 z-10 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-full shadow-lg transition-colors touch-manipulation"
        >
          New messages ↓
        </button>
      )}
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {virtualItems.map((virtualItem) => {
          const message = messages[virtualItem.index];
          return (
            <div
              key={message.id}
              data-index={virtualItem.index}
              ref={virtualizer.measureElement}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualItem.start}px)`,
              }}
              className="pb-4"
            >
              <MessageItem
                message={message}
                currentUserId={currentUserId}
                isEditing={editingMessageId === message.id}
                editContent={editContent}
                savingEdit={savingEdit && editingMessageId === message.id}
                onEditContentChange={onEditContentChange}
                onStartEdit={onStartEdit}
                onSaveEdit={onSaveEdit}
                onCancelEdit={onCancelEdit}
                onDelete={onDelete}
                onToggleReaction={onToggleReaction}
                onLoadWhoReacted={onLoadWhoReacted}
                deleting={deletingMessageId === message.id}
                commonEmojis={commonEmojis}
              />
            </div>
          );
        })}

        {/* Typing Indicator - positioned at bottom */}
        {typingUsers.size > 0 && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${virtualizer.getTotalSize()}px)`,
            }}
            className="pb-4"
          >
            <div className="flex items-start">
              <div className="bg-slate-700 rounded-lg px-4 py-2">
                <div className="text-sm text-slate-300">
                  {Array.from(typingUsers).join(', ')} {typingUsers.size === 1 ? 'is' : 'are'}{' '}
                  typing...
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
